/**
 * Automerge ↔ Monaco bidirectional sync, driven by a {@link TextFile}.
 * Granular splice operations via `Automerge.splice` so concurrent edits
 * merge sensibly; outbound writes go through `textFile.change(...)`, which
 * stamps the inode's mtime/size before resolving.
 */
import * as Automerge from "@automerge/automerge";
import type { editor } from "monaco-editor";
import type { TextFile, TextPatch } from "playground/fs/types";

export interface SetupOptions {
  /**
   * Optional change-message factory: if provided, each Automerge change
   * driven by Monaco edits will carry this message. Used by the activity
   * feed to attribute writes (e.g. `{ author: "user:..." }`).
   */
  message?: () => string | undefined;
}

export function setupAutomergeSync(
  editor: editor.IStandaloneCodeEditor,
  textFile: TextFile,
  options: SetupOptions = {},
) {
  let ignoreMonacoChanges = false;
  let ignoreAutomergeChanges = false;

  const monacoDisposable = editor.onDidChangeModelContent((event) => {
    if (ignoreMonacoChanges) return;
    ignoreAutomergeChanges = true;
    const message = options.message?.();
    void textFile
      .change(
        (doc) => {
          // Apply Monaco changes end-to-start so earlier splices don't shift
          // later offsets. Monaco offsets reference the pre-event state.
          const sorted = [...event.changes].sort(
            (a, b) => b.rangeOffset - a.rangeOffset,
          );
          for (const change of sorted) {
            Automerge.splice(
              doc,
              ["text"],
              change.rangeOffset,
              change.rangeLength,
              change.text,
            );
          }
        },
        message ? { message } : undefined,
      )
      .finally(() => {
        ignoreAutomergeChanges = false;
      });
  });

  const onChange = ({ patches }: { patches: TextPatch[] }) => {
    if (ignoreAutomergeChanges) return;
    const textPatches = patches.filter((p) => p.path[0] === "text");
    if (textPatches.length === 0) return;

    const model = editor.getModel();
    if (!model) return;

    ignoreMonacoChanges = true;
    for (const patch of textPatches) {
      if (patch.path.length < 2) continue;
      const index = patch.path[1] as number;
      let deleteLength = 0;
      if (patch.action === "del") deleteLength = patch.length || 1;

      let text = "";
      if (patch.action === "splice") text = patch.value || "";
      else if (patch.action === "insert") text = patch.values.join("");
      else if (patch.action !== "del") {
        throw new Error(
          `${patch.action} is unsupported by automerge/monaco sync`,
        );
      }

      const startPos = model.getPositionAt(index);
      const endPos = model.getPositionAt(index + deleteLength);
      editor.executeEdits("automerge", [
        {
          range: {
            startLineNumber: startPos.lineNumber,
            startColumn: startPos.column,
            endLineNumber: endPos.lineNumber,
            endColumn: endPos.column,
          },
          text,
        },
      ]);
    }
    ignoreMonacoChanges = false;
  };

  const unsubscribe = textFile.onChange(onChange);

  return () => {
    monacoDisposable.dispose();
    unsubscribe();
  };
}
